/**
 * Task #4034 — Itemised price breakdown lines.
 *
 * Replaces the previous "rolled-up Airbnb-style" summary pattern. Every
 * fee is now surfaced as its own line directly on booking surfaces (widget,
 * checkout, trip detail, request-change modal, mobile checkout) — no more
 * collapsing service / insurance / climate into a single per-unit headline.
 *
 * The penny-rounding contract is preserved: the breakdown rows always sum
 * exactly to `canonicalTotal` when supplied. Any reconciliation residue is
 * absorbed into the `rental_fee` line (the largest, least surprising row).
 *
 * `deriveRentalUnit` is kept as a standalone export because mobile and the
 * trip-detail page still need to resolve the hour-vs-day display unit
 * without building a full breakdown.
 */

/**
 * Structural quote shape — covers the fields this helper reads from
 * either the web or mobile `PriceQuote`. We keep it framework-free so
 * `lib/booking-rules` doesn't pull in a UI dep.
 */
export interface RolledUpQuoteInput {
  rentalFee: number;
  baseRentalFee?: number;
  serviceFee: number;
  insuranceFee: number;
  insurancePackageName?: string | null;
  deliveryFee: number;
  climateOffsetAmount?: number;
  refundableSurcharge?: number;
  numDays: number;
  rentalFeeBilling?: "hourly" | "daily-capped" | "daily-flat" | "daily" | "daily-custom";
  rentalFeeRate?: number;
  baseRate?: number;
  eventDailyCapRate?: number;
  /**
   * Persisted rental type ("driving" / "chauffeured" / "event"). Trip-based
   * surfaces (booking overview, conversation reservation panel, mobile
   * booking detail) don't carry the live quote's `rentalFeeBilling` signal —
   * they only have the persisted `rental_type` + `requested_hours`. When
   * `rentalFeeBilling` is absent, the shared unit derivation falls back to
   * `rentalType` (chauffeured / event) + a non-zero per-day hours count to
   * resolve the hour unit, so every surface agrees on hour-vs-day (task
   * #3394). Ignored when `rentalFeeBilling` is present — the server's
   * billing mode always wins.
   */
  rentalType?: string | null;
  /**
   * Per-day billable hours requested. Populated by the server's
   * `calculateQuote` for both `chauffeured` and `event` rental types
   * whenever billing is hourly. The historical name is kept for
   * backwards-compatibility; `hoursRequested` is the additive alias
   * (task #2274). Either one is honoured.
   */
  eventHoursRequested?: number;
  /** Additive alias of `eventHoursRequested` — see above. */
  hoursRequested?: number;
  tax?: {
    applicable: boolean;
    amount: number;
    status?: "ok" | "pending" | "unavailable" | "none";
  };
}

export type RentalUnit = "day" | "night" | "hour";

export interface RolledUpPriceOptions {
  /** Whether the climate-offset add-on toggle is on. */
  includeClimateOffset?: boolean;
  /** Whether the renter has the refundable-cancellation policy selected. */
  includeRefundableSurcharge?: boolean;
  /**
   * Optional coupon/discount amount to subtract from the reconciled total
   * (and surface as a `coupon` line in the breakdown). The discount is
   * applied AFTER per-unit reconciliation so the headline `unitPrice`
   * still reflects the standard rolled-up rate.
   */
  couponDiscount?: number;
  /** Optional coupon code to surface alongside the discount line. */
  couponCode?: string | null;
  /**
   * Task #8163 — applied Pro Owner booking discount (10% Standard/Classic,
   * 15% Premier off the rental fee). Emitted as a negative
   * `pro_owner_discount` line, ordered just before the coupon line so the
   * discounts sit together at the end of the breakdown. Absent/0 ⇒ no line
   * (bookings without a discount render exactly as before).
   */
  proOwnerDiscount?: number;
  /** Pro Owner tier ("standard" / "classic" / "premier") for the line detail. */
  proOwnerTier?: string | null;
  /**
   * Optional server-authoritative total (e.g. `quote.paymentDue` /
   * `quote.totalIncludingTax` / `trip.payment_due`). When provided,
   * `total` is anchored to this value and any reconciliation residue is
   * absorbed into the `rental_fee` line so the breakdown rows sum exactly.
   * This guarantees the displayed lines never drift from the headline
   * total the renter sees on the surrounding page.
   */
  canonicalTotal?: number | null;
  /**
   * Emit a zero-value delivery line when the renter has actively selected
   * delivery. Callers must derive this from selection state, not merely from
   * a zero quote, so pickup-only bookings do not claim free delivery.
   */
  includeZeroDelivery?: boolean;
}

/** A single itemised price line returned by `buildPriceBreakdownLines`. */
export interface PriceLine {
  /** i18n key suffix for this row (e.g. "rental_fee", "service_fee"). */
  key:
    | "rental_fee"
    | "service_fee"
    | "insurance"
    | "climate_offset"
    | "refundable_surcharge"
    | "delivery"
    | "tax"
    | "pro_owner_discount"
    | "coupon";
  amount: number;
  /** Optional secondary label (e.g. insurance package name, coupon code). */
  detail?: string | null;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Inputs to the single shared hour-vs-day unit derivation (task #3394).
 * This is the ONE mechanism every price-breakdown surface — web + mobile,
 * checkout + booking overview + conversation panel — funnels through, so a
 * chauffeured / event-hourly booking renders "rate/hour × N" everywhere
 * and a self-drive / daily-billed booking renders "rate/day × N"
 * everywhere.
 */
export interface RentalUnitInput {
  /**
   * Server-authoritative billing mode from the live quote. When present it
   * always wins (e.g. an event that hit the daily cap is `daily-capped`
   * and must render per-day even though hours were requested).
   */
  rentalFeeBilling?: "hourly" | "daily-capped" | "daily-flat" | "daily" | "daily-custom" | null;
  /** Persisted rental type — the fallback signal when no billing mode. */
  rentalType?: string | null;
  /** Per-day billable hours (server `hoursRequested` or persisted `requested_hours`). */
  hoursPerDay?: number | null;
  /** Chargeable-day count for the booking window. */
  numDays?: number | null;
}

/**
 * Resolve the rental unit (hour vs day) and the total unit count from
 * whatever signals a surface has. Order of precedence:
 *   1. `rentalFeeBilling === "hourly"` (live quote) → hour.
 *   2. No billing mode, but `rentalType` is chauffeured / event AND a
 *      non-zero per-day hours count → hour (trip-based surfaces). The
 *      booking RPC only persists `requested_hours` for genuinely hourly
 *      bookings, so daily-capped / self-drive rows carry 0 here and stay
 *      on the day unit.
 *   3. Otherwise → day.
 * Hour count is `hoursPerDay × numDays`; a hourly booking that is missing
 * its hours field falls back to 1 hour/day rather than misrepresenting the
 * per-day total as the hourly rate.
 */
export function deriveRentalUnit(
  input: RentalUnitInput,
): { unit: "day" | "hour"; unitCount: number } {
  const numDays = Math.max(input.numDays || 1, 1);
  const hoursPerDayRaw = input.hoursPerDay && input.hoursPerDay > 0 ? input.hoursPerDay : 0;
  const billing = input.rentalFeeBilling;
  const rentalType = (input.rentalType ?? "").toLowerCase();
  const isHourlyType = rentalType === "chauffeured" || rentalType === "event";
  const isHourly = billing === "hourly" || (billing == null && isHourlyType && hoursPerDayRaw > 0);
  if (isHourly) {
    const hoursPerDay = hoursPerDayRaw > 0 ? hoursPerDayRaw : 1;
    return { unit: "hour", unitCount: Math.max(hoursPerDay * numDays, 1) };
  }
  return { unit: "day", unitCount: numDays };
}

/**
 * Build an ordered array of itemised price lines for a booking quote.
 *
 * Every fee is returned as its own `PriceLine` — nothing is "rolled up"
 * into a per-unit headline. Display order (task #2657):
 *   rental_fee → insurance → climate_offset → refundable_surcharge →
 *   delivery → service_fee → tax → coupon
 *
 * When `opts.canonicalTotal` is supplied the rows are reconciled so they
 * sum exactly to that server-authoritative value (any penny residue lands
 * on the `rental_fee` line). This guarantees the breakdown rows displayed
 * on a page never drift from the page's own total figure.
 */
export function buildPriceBreakdownLines(
  quote: RolledUpQuoteInput & { currency?: string },
  opts: RolledUpPriceOptions = {},
): PriceLine[] {
  const rental = Number(quote.baseRentalFee ?? quote.rentalFee ?? 0) || 0;
  const service = Number(quote.serviceFee || 0);
  const insurance = Number(quote.insuranceFee || 0);
  const climate = opts.includeClimateOffset ? Number(quote.climateOffsetAmount || 0) : 0;
  const refundable = opts.includeRefundableSurcharge ? Number(quote.refundableSurcharge || 0) : 0;
  const delivery = Number(quote.deliveryFee || 0);
  const taxApplicable = !!quote.tax?.applicable;
  const taxStatus = quote.tax?.status ?? null;
  const taxAmount = taxApplicable && (taxStatus === "ok" || taxStatus == null)
    ? Number(quote.tax?.amount || 0)
    : 0;
  const coupon = Math.max(0, Number(opts.couponDiscount || 0));
  const proOwner = Math.max(0, Number(opts.proOwnerDiscount || 0));

  let total: number;
  if (opts.canonicalTotal != null) {
    total = round2(Number(opts.canonicalTotal));
  } else {
    total = round2(rental + service + insurance + climate + refundable + delivery + taxAmount - coupon - proOwner);
  }

  const componentSum = round2(rental + service + insurance + climate + refundable + delivery + taxAmount - coupon - proOwner);
  const residue = round2(total - componentSum);
  const reconciledRental = round2(rental + residue);

  const lines: PriceLine[] = [{ key: "rental_fee", amount: reconciledRental }];
  if (insurance > 0) {
    lines.push({
      key: "insurance",
      amount: insurance,
      detail: quote.insurancePackageName ?? null,
    });
  }
  if (climate > 0) lines.push({ key: "climate_offset", amount: climate });
  if (refundable > 0) lines.push({ key: "refundable_surcharge", amount: refundable });
  if (delivery > 0 || opts.includeZeroDelivery) lines.push({ key: "delivery", amount: delivery });
  if (service > 0) lines.push({ key: "service_fee", amount: service });
  if (taxAmount > 0) lines.push({ key: "tax", amount: taxAmount });
  if (proOwner > 0) {
    lines.push({
      key: "pro_owner_discount",
      amount: -proOwner,
      detail: opts.proOwnerTier ?? null,
    });
  }
  if (coupon > 0) {
    lines.push({
      key: "coupon",
      amount: -coupon,
      detail: opts.couponCode ?? null,
    });
  }

  return lines;
}
